import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { customerPaymentsAPI } from '../lib/api';

const normalizeCustomerPaymentPayload = (paymentData = {}) => {
  const customerId = paymentData.customerId || paymentData.CustomerId;
  const companyId = paymentData.companyId || paymentData.CompanyId;
  const accountId = paymentData.accountId || paymentData.AccountId;
  const amount = paymentData.amount ?? paymentData.Amount;
  const transactionDate = paymentData.transactionDate || paymentData.TransactionDate;
  const paymentReference = paymentData.paymentReference || paymentData.PaymentReference || '';
  const statusId = paymentData.statusId || paymentData.StatusId || 1;
  const addedBy = paymentData.addedBy || paymentData.AddedBy;

  if (!customerId) throw new Error('Customer ID is required before creating a payment.');
  if (!companyId) throw new Error('Company ID is required before creating a payment.');
  if (!accountId) throw new Error('Account ID is required before creating a payment.');
  if (amount === undefined || amount === null || Number.isNaN(Number(amount))) {
    throw new Error('A valid payment amount is required before creating a payment.');
  }
  if (!transactionDate) throw new Error('Transaction date is required before creating a payment.');
  if (!addedBy) throw new Error('User ID is required before creating a payment.');

  return {
    CustomerId: customerId,
    CompanyId: companyId,
    AccountId: accountId,
    Amount: Number(amount),
    TransactionDate: transactionDate,
    PaymentReference: paymentReference,
    StatusId: statusId,
    AddedBy: addedBy,
  };
};

// Hook to get all customer payments for a company
export const useCustomerPayments = (companyId, startDate, endDate) => {
  return useQuery({
    queryKey: ['customerPayments', companyId, startDate, endDate],
    queryFn: () => customerPaymentsAPI.getAll(companyId, startDate, endDate),
    select: (response) => response.data?.Data || [],
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Hook to get payments for a specific customer
export const useCustomerPaymentsByCustomer = (companyId, customerId, startDate, endDate) => {
  return useQuery({
    queryKey: ['customerPayments', companyId, customerId, startDate, endDate],
    queryFn: () => customerPaymentsAPI.getByCustomer(companyId, customerId, startDate, endDate),
    select: (response) => response.data?.Data || [],
    enabled: !!companyId && !!customerId && !!startDate && !!endDate,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Hook to create a new customer payment
export const useCreateCustomerPayment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (paymentData) => customerPaymentsAPI.create(normalizeCustomerPaymentPayload(paymentData)),
    onSuccess: (response, variables) => {
      // Invalidate and refetch customer payments
      queryClient.invalidateQueries({ queryKey: ['customerPayments'] });
      
      // Also invalidate customer data as payment might affect balances
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      
      console.log('Customer payment created successfully:', response.data);
    },
    onError: (error) => {
      console.error('Error creating customer payment:', error);
    },
  });
};

// Hook to delete a customer payment
export const useDeleteCustomerPayment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (paymentId) => customerPaymentsAPI.delete(paymentId),
    onSuccess: (response, paymentId) => {
      // Invalidate and refetch customer payments
      queryClient.invalidateQueries({ queryKey: ['customerPayments'] });
      
      // Also invalidate customer data as payment deletion might affect balances
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      
      console.log('Customer payment deleted successfully:', paymentId);
    },
    onError: (error) => {
      console.error('Error deleting customer payment:', error);
    },
  });
};